import React from 'react';
import styled, { css } from 'styled-components';
import { lighten, darken as darkenFn } from 'polished';
import COLORS from '../../../constants/COLORS';

interface Props {
  color?: string;
  plain?: boolean;
  compact?: boolean;
  danger?: boolean;
  info?: boolean;
  darken?: boolean;
  hollow?: boolean;
  target?: string;
  href?: string;
  tag?: string;
  type?: string;
  children: any;
  onClick?: (props: any) => void;
  disabled?: boolean;
}

const Button = ({ children, type = 'button', tag = 'button', href, target, ...props }: Props) => (
  <Wrapper
    tag={tag}
    type={tag === 'button' ? type : undefined}
    href={href}
    target={target}
    rel={target === '_blank' ? 'noopener noreferrer' : undefined}
    {...props}
  >
    {children}
  </Wrapper>
);

interface WrapperProps {
  plain?: boolean;
  tag: string;
  children: any;
  compact?: boolean;
  darken?: boolean;
  hollow?: boolean;
  danger?: boolean;
  info?: boolean;
  type?: string;
  href?: string;
  target?: string;
  rel?: string;
}

const Wrapper = styled(({ tag, children, ...rest }: WrapperProps) => React.createElement(tag, rest, children))`
  width: 100%;
  display: block;
  padding: 13px 3px;
  background: ${({ plain, theme }) =>
    plain ? 'transparent' : theme.buttonBackground || COLORS.BLUE};
  border-radius: 8px;
  font-size: 16px;
  line-height: 21px;
  color: ${({ theme }) => theme.buttonColor || '#fff'};
  outline: none;
  border: 0;
  text-decoration: none;
  text-align: center;
  cursor: pointer;

  ${({ theme, plain }) =>
    !plain &&
    css`
      &:hover {
        background: ${lighten(0.1, theme.buttonBackground || COLORS.BLUE)};
      }

      &:focus {
        background: ${darkenFn(0.1, theme.buttonBackground || COLORS.BLUE)};
      }
    `}

  ${({ compact }) =>
    compact &&
    css`
      width: auto;
      padding: 9px 16px;
    `}

  ${({ theme, darken }) =>
    darken &&
    css`
      background-color: ${theme.buttonBackground || COLORS.DARK_BLUE};
      border-color: ${theme.buttonBackground || COLORS.DARK_BLUE};

      &:hover {
        background: ${lighten(0.1, theme.buttonBackground || COLORS.DARK_BLUE)};
      }

      &:focus {
        background: ${darkenFn(0.1, theme.buttonBackground || COLORS.DARK_BLUE)};
      }
    `}

  ${({ hollow, theme }) =>
    hollow &&
    css`
      border: 1px solid ${theme.buttonBackground || COLORS.DARK_BLUE};
      background: transparent;
      color: ${theme.buttonBackground || COLORS.DARK_BLUE};

      &:hover {
        border-color: ${lighten(0.1, theme.buttonBackground || COLORS.DARK_BLUE)};
        background: transparent;
      }

      &:focus {
        border-color: ${darkenFn(0.1, theme.buttonBackground || COLORS.DARK_BLUE)};
        background: transparent;
      }
    `}

  ${({ danger }) =>
    danger &&
    css`
      color: ${COLORS.RED};

      &:hover {
        background: rgba(235, 87, 87, 0.08);
      }
    `}

  ${({ info }) =>
    info &&
    css`
      color: ${COLORS.BLUE};

      &:hover {
        background: rgba(47, 128, 237, 0.08);
      }
    `}
`;

export default Button;
